"use client";
import { useSyncExternalStore } from "react";
import { LOCAL_STORAGE_EVENT } from "./storageEvents";

// Tarayıcıya özgü bir değeri (localStorage, matchMedia, URL) render sırasında
// okumak için (F19). Eskiden bu değerler bir efektte okunup state'e
// yansıtılıyordu: her mount'ta garantili ikinci render ve `set-state-in-effect`
// lint hatası. `useSyncExternalStore` ile sunucuda ve hydration'da `fallback`
// döner, hydration biter bitmez gerçek değerle yeniden çizilir (hydration
// uyuşmazlığı yok), `subscribe` verilirse değişimler de izlenir.
//
// `read` her render'da çağrılır: ilkel (string/number/boolean/null) döndürmeli;
// her seferinde yeni nesne üretmek sonsuz döngüye yol açar.
//
// ⚠️ Mount efekti bu değerin FALLBACK'ini görür: hydration render'ı ile ilk
// passive efekt aynı geçişte koşar, gerçek değer ancak sonraki render'da gelir.
// Render için bu hook, tek-seferlik tetikleyici (URL token'ıyla istek atmak,
// oturum yoksa yönlendirmek) için efektte değeri doğrudan oku
// (`new URLSearchParams(window.location.search)`, `getToken()`); F33'te
// e-posta doğrulama linki tam bu yüzden hiç çalışmıyordu.
type Subscribe = (onChange: () => void) => () => void;

const noSubscribe: Subscribe = () => () => {};

export function useClientValue<T extends string | number | boolean | null>(
  read: () => T,
  fallback: T,
  subscribe: Subscribe = noSubscribe,
): T {
  return useSyncExternalStore(subscribe, read, () => fallback);
}

// `storage` yalnızca DİĞER sekmelerde ateşlenir; aynı sekmedeki yazımlar
// `announceStorage` (lib/storageEvents) ile duyurulur.
export { announceStorage } from "./storageEvents";

export const subscribeStorage: Subscribe = (onChange) => {
  window.addEventListener("storage", onChange);
  window.addEventListener(LOCAL_STORAGE_EVENT, onChange);
  return () => {
    window.removeEventListener("storage", onChange);
    window.removeEventListener(LOCAL_STORAGE_EVENT, onChange);
  };
};

export function useLocalStorageValue(key: string): string | null {
  return useClientValue(
    () => { try { return localStorage.getItem(key); } catch { return null; } },
    null,
    subscribeStorage,
  );
}

const subscribeHistory: Subscribe = (onChange) => {
  window.addEventListener("popstate", onChange);
  return () => window.removeEventListener("popstate", onChange);
};

// URL sorgu parametresi, Suspense sınırı gerektirmeden (Next 16'da
// `useSearchParams` statik sayfalarda Suspense ister). `history.replaceState`
// ile temizlenen bir parametre bir sonraki render'da null okunur; değeri
// saklaması gereken çağıran onu kendi state'ine (bir efektin `.then`'inde ya da
// olay işleyicisinde) alır.
export function useSearchParam(name: string): string | null {
  return useClientValue(() => new URLSearchParams(window.location.search).get(name), null, subscribeHistory);
}

const subscribeMedia = (query: string): Subscribe => (onChange) => {
  const mq = window.matchMedia(query);
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
};

export function useMediaQuery(query: string): boolean {
  return useClientValue(() => window.matchMedia(query).matches, false, subscribeMedia(query));
}
